// apps/merchant-v3/src/components/security/SuspendOverlay.tsx
//
// Phase 6 Wave 12 Task 43 — Full-page overlay rendered by RequireAuth
// when `useUser().merchantSuspended === true` AND the merchant has
// navigated to a route that is NOT on the allowlist (
// merchant-suspend lockdown). Allowed routes: `/settings/security/*`
// (so merchants can rotate password / 2FA), `/support` and the
// /403/500/404 error pages so legitimate fallbacks still resolve.
//
// The persistent SuspendBanner (Task 42) continues to render
// at the top of every authenticated page; THIS component is the
// stronger lockdown surface that prevents the merchant from interacting
// with the rest of the app. The overlay surfaces a Contact Support CTA
// pointing at `mailto:` or `/support` (env-gated, mirrors SuspendBanner).
//
// vg-binding: PLAN:task-43
// vg-binding: P6.D-114
// vg-binding: P6.D-128
// vg-binding: P6.D-204
// vg-binding: P6.D-248

import type { JSX } from 'react';
import { Lock } from 'lucide-react';

import { useUser } from '@/store/user';
import { useTranslation } from '@/lib/i18n';

const SUPPORT_HREF: string =
  (import.meta.env as Record<string, string | undefined>).VITE_SUPPORT_ENABLED ===
  'true'
    ? '/tickets'
    : 'mailto:support@printway.work';

/**
 * Routes that remain accessible while a merchant is suspended.
 * Anything matching one of these prefixes bypasses the overlay so the
 * merchant can still open Security settings and contact Support.
 */
const ALLOWED_PREFIXES: readonly string[] = [
  '/settings/security',
  '/tickets',
  '/403',
  '/404',
  '/500',
];

export function isSuspendAllowedPath(path: string): boolean {
  return ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function SuspendOverlay(): JSX.Element | null {
  const user = useUser();
  const { t } = useTranslation('common');

  if (!user?.merchantSuspended) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('security.suspend.overlay.title', {
        defaultValue: 'Account suspended',
      })}
      data-testid="suspend-overlay"
      /* dark-ok: overlay scrim stays a dark wash in both themes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4 py-12 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-lg border border-red-200 bg-pw-card p-6 text-center shadow-xl">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Lock className="h-7 w-7" aria-hidden />
        </div>
        <h1
          data-testid="suspend-overlay-title"
          className="mt-5 text-lg font-semibold text-pw-text"
        >
          {t('security.suspend.overlay.title', {
            defaultValue: 'Tài khoản đang tạm ngưng',
          })}
        </h1>
        <p className="mt-3 text-sm text-pw-text-muted">
          {t('security.suspend.overlay.body', {
            defaultValue: 'Access restricted. Contact Admin Support.',
          })}
        </p>
        {user.merchantSuspendReason ? (
          <p className="mt-2 text-xs italic text-pw-text-muted">
            {user.merchantSuspendReason}
          </p>
        ) : null}
        <a
          href={SUPPORT_HREF}
          data-testid="suspend-overlay-contact"
          className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-brand-500 px-5 text-sm font-medium text-white hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1"
        >
          {t('security.suspend.overlay.cta', {
            defaultValue: 'Liên hệ Support',
          })}
        </a>
      </div>
    </div>
  );
}

export default SuspendOverlay;
